// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========

import { features } from '@/lib/edition';
import {
  appendNote,
  createNote,
  deleteNote,
  fetchNotes,
  NoteCreatePayload,
  NoteData,
  NoteUpdatePayload,
  updateNote,
} from '@/service/notesApi';
import { registerLogoutHook } from '@/lib/logoutHooks';
import { create } from 'zustand';

export type SourceFilter = 'all' | 'agent' | 'user';
export type NoteStatus = 'idle' | 'loading' | 'ready' | 'error';

function messageOf(error: unknown): string {
  return error instanceof Error && error.message
    ? error.message
    : 'Request failed';
}

interface NoteState {
  notes: NoteData[];
  status: NoteStatus;
  error: string | null;
  sourceFilter: SourceFilter;
  /** Set after a successful delete, so attachments can drop that note. */
  removed: { id: number } | null;

  fetchNotes: (projectId?: string, taskId?: string) => Promise<void>;
  setSourceFilter: (filter: SourceFilter) => void;
  createNote: (data: NoteCreatePayload) => Promise<NoteData | null>;
  updateNote: (
    noteId: number,
    data: NoteUpdatePayload
  ) => Promise<NoteData | null>;
  appendNote: (noteId: number, content: string) => Promise<NoteData | null>;
  removeNote: (noteId: number) => Promise<boolean>;
  /** Forget everything (sign-out). */
  reset: () => void;
}

/**
 * Server-backed notes. Cloud edition only: in the community edition every
 * action is a no-op and nothing is requested. No method throws; a failure
 * sets `status: 'error'` with a message and keeps the list that was loaded.
 */
export const useNoteStore = create<NoteState>((set, get) => ({
  notes: [],
  status: 'idle',
  error: null,
  sourceFilter: 'all',
  removed: null,

  fetchNotes: async (projectId, taskId) => {
    if (!features.notes) return;
    set({ status: 'loading', error: null });
    try {
      const { sourceFilter } = get();
      const source = sourceFilter === 'all' ? undefined : sourceFilter;
      const notes = await fetchNotes(projectId, taskId, source);
      set({
        notes: Array.isArray(notes) ? notes : [],
        status: 'ready',
        error: null,
      });
    } catch (error) {
      set({ status: 'error', error: messageOf(error) });
    }
  },

  setSourceFilter: (filter) => set({ sourceFilter: filter }),

  createNote: async (data) => {
    if (!features.notes) return null;
    try {
      const note = await createNote(data);
      set((state) => ({ notes: [note, ...state.notes], error: null }));
      return note;
    } catch (error) {
      set({ error: messageOf(error) });
      return null;
    }
  },

  updateNote: async (noteId, data) => {
    if (!features.notes) return null;
    try {
      const updated = await updateNote(noteId, data);
      set((state) => ({
        notes: state.notes.map((n) => (n.id === noteId ? updated : n)),
        error: null,
      }));
      return updated;
    } catch (error) {
      set({ error: messageOf(error) });
      return null;
    }
  },

  appendNote: async (noteId, content) => {
    if (!features.notes) return null;
    try {
      const updated = await appendNote(noteId, content);
      set((state) => ({
        notes: state.notes.map((n) => (n.id === noteId ? updated : n)),
        error: null,
      }));
      return updated;
    } catch (error) {
      set({ error: messageOf(error) });
      return null;
    }
  },

  removeNote: async (noteId) => {
    if (!features.notes) return false;
    try {
      await deleteNote(noteId);
      set((state) => ({
        notes: state.notes.filter((n) => n.id !== noteId),
        removed: { id: noteId },
        error: null,
      }));
      return true;
    } catch (error) {
      set({ error: messageOf(error) });
      return false;
    }
  },

  reset: () =>
    set({
      notes: [],
      status: 'idle',
      error: null,
      sourceFilter: 'all',
      removed: null,
    }),
}));

// A signed-out user's notes must not be visible to the next one.
registerLogoutHook(() => useNoteStore.getState().reset());
